import { Config } from '@src/core/Config';

const N = Config.sim.maxEntities;

export enum Team {
  Neutral = 0,
  Player = 1,
  Enemy = 2,
}

export enum EnemyKind {
  Imp = 0,
  Golem = 1,
  /** Screamer skull: darts in fast lunges, dies to a touch. */
  Skull = 2,
  /** Hex slime: hops, tanky for its size, leaves a stain. */
  Slime = 3,
  /** Rune bat: orbits just out of reach, then dives. */
  Bat = 4,
  /** Wall-mounted gargoyle: sits in the stone and spits aimed bolts. */
  Gargoyle = 5,
  /** Floor boss: the exit room's archmage. Driven by BossSystem, sub-kind in BossTag.kind. */
  Boss = 6,
  /** Hollow wraith: drifts in, fades out, reforms behind the wizard and lunges. */
  Wraith = 7,
  /** Imp hexer: keeps its distance and lobs aimed hex bolts. The first mobile caster. */
  Hexer = 8,
  /** Cinder skull: a slow, relentless bomb. Bursts on contact or death and scorches a radius. */
  Cinder = 9,
  /** Blight slime: heavy hopper that splits into two hex slimes when it pops. */
  Blight = 10,
  /** Ember brute: a lean golem that winds up and charges across the room. */
  Brute = 11,
  /** Wall-mounted acid spitter: gargoyle cousin that fires a three-bolt fan. */
  Spitter = 12,
  /** Mimic: a treasure chest that is not. Sleeps until a wizard gets greedy. */
  Mimic = 13,
}

/**
 * Boss roster. The first three are the archmages that guard floor exits; the rest are the
 * room champions that show up after the portals fall in every other fight room.
 */
export enum BossKind {
  /** Obsidian floors: Vormund the Unburied. */
  Lich = 0,
  /** Verdant floors: Sylvara the Thornqueen. */
  Druid = 1,
  /** Infernal floors: Ignatius Embercrown. */
  Magus = 2,
  /** Champions. */
  Warlord = 3,
  SkullKing = 4,
  Colossus = 5,
  Matriarch = 6,
  Sentinel = 7,
  /** Later champions: an imp hexer grown up, a cinder titan, and a wraith matron. */
  Hexweaver = 8,
  Ashmaw = 9,
  Bride = 10,
  COUNT,
}
/** Number of archmage kinds at the front of BossKind. */
export const ARCHMAGE_COUNT = 3;

/** Sprite archetypes: each maps to one InstancedMesh pool in the renderer. */
export enum SpriteId {
  Player = 0,
  Imp,
  Golem,
  Skull,
  Slime,
  Bat,
  PickupHeart,
  Portal,
  Dragon,
  BoltWand,
  BoltFire,
  BoltPhase,
  Shockwave,
  PickupCleaver,
  PickupLightning,
  PickupCrossbow,
  Gem,
  PrizeCabbage,
  PrizeHat,
  PrizeChalice,
  PrizeGoldChest,
  PrizeSpellbook,
  PrizeCrystalBall,
  PrizePotion,
  PrizeHourglass,
  Ghost,
  Shadow,
  MuzzleWand,
  MuzzleFire,
  MuzzlePhase,
  Slash,
  LightningHit,
  Explosion,
  Goo,
  Smoke,
  Spark,
  Torch,
  FlameJet,
  Gargoyle,
  BossLich,
  BossDruid,
  BossMagus,
  /** Boss spell shots: rune shard, orbiting orb, homing wisp. */
  BossBolt,
  BossOrb,
  BossWisp,
  /** Room champions. Warlord and SkullKing have their own sheets; the rest reuse enemy art scaled up. */
  BossWarlord,
  BossSkullKing,
  BossColossus,
  BossMatriarch,
  BossSentinel,
  /** Champion shots: hurled rubble, lobbed acid, launched skull. */
  BossRubble,
  BossAcid,
  BossSkull,
  /**
   * Later guests. Each has its own sheet in tools/manifest/sheets.json; until that art is
   * generated AnimDefs.ART_FALLBACK borrows a cousin's frames and the spawn tint sells it.
   */
  Wraith,
  Hexer,
  Cinder,
  Blight,
  Brute,
  Spitter,
  Mimic,
  BossHexweaver,
  BossAshmaw,
  BossBride,
  /** Floor mine: a planted charge that fuses when stepped on. */
  Mine,
  /** Rare Cataclysm Orb pickup. */
  PickupBomb,
  /** Rune Ward pickup disc. */
  PickupShield,
  /** Orbiting ward blade. */
  WardBlade,
  /** Mine / Cataclysm blast, distinct from the generic arcane explosion. */
  BombBlast,
  COUNT,
}

/** Animation clips. Not every sprite has every clip; the registry falls back to Idle. */
export enum ClipId {
  Idle = 0,
  Run,
  Cast,
  Hurt,
  Death,
  COUNT,
}

export enum PickupKind {
  Weapon = 0,
  Gem = 1,
  Prize = 2,
  /** Rune heart; sub = hp restored. */
  Heart = 3,
  /** Off-hand Rune Ward. Does not replace the held weapon. */
  Shield = 4,
  /** Gale Step move-speed perk. */
  Swift = 5,
}

/** Player.offhand values. */
export const OFFHAND_NONE = 0;
export const OFFHAND_SHIELD = 1;

/** Dying.flag values. */
export const DYING_REAP = 1;
export const DYING_ANIM = 2;

export const Position = { x: new Float32Array(N), y: new Float32Array(N) };
export const PrevPosition = { x: new Float32Array(N), y: new Float32Array(N) };
export const Velocity = { x: new Float32Array(N), y: new Float32Array(N) };
export const Radius = { r: new Float32Array(N) };
export const Health = { hp: new Float32Array(N), max: new Float32Array(N), invuln: new Float32Array(N) };
export const TeamTag = { team: new Uint8Array(N) };
export const Sprite = {
  id: new Uint8Array(N),
  scale: new Float32Array(N),
  rot: new Float32Array(N),
  tint: new Uint32Array(N),
  alpha: new Float32Array(N),
  z: new Float32Array(N),
  /** Procedural deformation, -1 (tall) .. 1 (wide), decays to 0. */
  squash: new Float32Array(N),
  /** Scale pop on spawn, 1 -> 0. */
  pop: new Float32Array(N),
  /** Facing: 1 right, -1 left (front-view art flips instead of rotating). */
  face: new Int8Array(N),
  /** Hit flash, 1 (white) -> 0, decays fast. */
  flash: new Float32Array(N),
  /** Impact jolt: the sprite is shoved along this offset (world units) and it decays to 0. */
  joltX: new Float32Array(N),
  joltY: new Float32Array(N),
};
export const Anim = {
  clip: new Uint8Array(N),
  frame: new Uint8Array(N),
  time: new Float32Array(N),
  speed: new Float32Array(N),
  done: new Uint8Array(N),
  /** Set by gameplay to force a clip until it finishes (Hurt, Cast, Death). */
  lock: new Uint8Array(N),
};
export const Lifetime = { t: new Float32Array(N) };
export const Damage = { amount: new Float32Array(N), spawnerMult: new Float32Array(N) };
export const Pierce = { left: new Int16Array(N) };
export const Knockback = { force: new Float32Array(N) };
export const Player = {
  inputIndex: new Uint8Array(N),
  aimX: new Float32Array(N),
  aimY: new Float32Array(N),
  firing: new Uint8Array(N),
  /** 1 while the player is actively aiming (fire held, right stick deflected, or the mouse moved recently). */
  aiming: new Uint8Array(N),
  moveX: new Float32Array(N),
  moveY: new Float32Array(N),
  score: new Int32Array(N),
  cornerTime: new Float32Array(N),
  idleTime: new Float32Array(N),
  dead: new Uint8Array(N),
  /** Recoil kick applied to the sprite on each shot, decays. */
  recoil: new Float32Array(N),
  /** Off-hand slot (OFFHAND_*). Parallel to Weapon.typeId; never swaps the gun. */
  offhand: new Uint8Array(N),
  offhandTime: new Float32Array(N),
  /** Seconds left on Gale Step. Independent of the ward. */
  swiftTime: new Float32Array(N),
  /** Seconds left in a double-tap dash. */
  dashTime: new Float32Array(N),
  dashX: new Float32Array(N),
  dashY: new Float32Array(N),
};
export const Weapon = {
  typeId: new Uint8Array(N),
  timeLeft: new Float32Array(N),
  overcharge: new Float32Array(N),
  cooldown: new Float32Array(N),
  pulse: new Float32Array(N),
};
export const Enemy = {
  kind: new Uint8Array(N),
  stagger: new Float32Array(N),
  seed: new Float32Array(N),
  /** Behavior timer for kinds that move in bursts (skull lunge, slime hop, bat dive, imp fake, golem stomp). */
  beat: new Float32Array(N),
  /**
   * Behavior sub-state, meaning depends on kind:
   * skull/slime/bat/blight 0 idle 1 burst; imp 0 chase 1 fake 2 wind 3 lunge; golem 0 walk 1 wind 2 stomp;
   * wraith 0 drift 1 fade 2 reform 3 lunge; hexer 0 kite 1 wind; cinder 0 chase;
   * brute 0 walk 1 wind 2 charge 3 dazed; mimic 0 dormant 1 waking 2 hunt; wall guns 0 idle 1 wind.
   */
  mode: new Uint8Array(N),
  /** Locked heading for kinds that commit to a direction (brute charge, wraith lunge). */
  lockX: new Float32Array(N),
  lockY: new Float32Array(N),
};
export const Spawner = { timer: new Float32Array(N), spawned: new Int32Array(N), interval: new Float32Array(N) };
export const Pickup = {
  kind: new Uint8Array(N),
  sub: new Uint8Array(N),
  /** Seconds left of frictionless glide (supply drops floating in through a gate). */
  drift: new Float32Array(N),
};
export const RECENT_HITS = 8;
export const Projectile = {
  owner: new Int32Array(N),
  weapon: new Uint8Array(N),
  grow: new Float32Array(N),
  /** Ring of recently hit entity ids so piercing shots don't re-hit every tick. */
  recent: new Int32Array(N * RECENT_HITS),
  recentCount: new Uint8Array(N),
};
export const DragonTag = {
  speed: new Float32Array(N),
  breathTimer: new Float32Array(N),
  /** 0 hunting, 1 winding up a breath, 2 breathing. */
  phase: new Uint8Array(N),
  phaseTime: new Float32Array(N),
  /** Fake altitude for the wing shadow. */
  bob: new Float32Array(N),
  /** Flames already spawned during the current breath. */
  breathed: new Uint8Array(N),
  /** Entity id of the live flame jet FX, or -1. */
  jet: new Int32Array(N),
};
/** Orbit slots per boss (orbiting orbs the boss steers before releasing). */
export const BOSS_ORBS = 12;
export const BossTag = {
  kind: new Uint8Array(N),
  /** 0 arriving, 1 fighting, 2 dying (see BossPhase in Boss.ts). */
  phase: new Uint8Array(N),
  phaseTime: new Float32Array(N),
  /** Current spell pattern and where it is in its script. */
  pattern: new Uint8Array(N),
  step: new Uint8Array(N),
  stepTime: new Float32Array(N),
  /** Seconds left before the next pattern starts. */
  rest: new Float32Array(N),
  /** Patterns cast so far this fight (drives blink cadence and pattern rotation). */
  casts: new Uint16Array(N),
  /** Working angle for radial volleys and beam sweeps. */
  angle: new Float32Array(N),
  /** Hover target the boss drifts toward. */
  targetX: new Float32Array(N),
  targetY: new Float32Array(N),
  blinkCooldown: new Float32Array(N),
  /** 1 once the half-health line has fired. */
  halfCalled: new Uint8Array(N),
  /** Bob phase for the float. */
  bob: new Float32Array(N),
  /** Rendered altitude in wu (hover height, crash-down fall, swoop arc). */
  lift: new Float32Array(N),
  /** Entity ids of live orbiting orbs (-1 = empty). */
  orbs: new Int32Array(N * BOSS_ORBS),
  orbCount: new Uint8Array(N),
  /** Death crescendo bookkeeping: last burst time. */
  burstAt: new Float32Array(N),
  /** Champion bookkeeping: locked charge/dive direction, splits done, summons alive, enrage flag. */
  lockX: new Float32Array(N),
  lockY: new Float32Array(N),
  splits: new Uint8Array(N),
  enraged: new Uint8Array(N),
  /** Global room index the boss was summoned in (drives tuning). */
  room: new Uint16Array(N),
  /** HP multiplier baked at spawn (treasure rooms get a lighter champion). */
  hpMult: new Float32Array(N),
};
/** Pure visual entities (muzzle flashes, explosions): Sprite + Anim + Lifetime. */
export const FxTag = { flag: new Uint8Array(N) };
/** Marker for entities that should be recycled: DYING_REAP now, DYING_ANIM after the death clip. */
export const Dying = { flag: new Uint8Array(N) };
/** 0 idle, 1 fuse lit. */
export const MINE_IDLE = 0;
export const MINE_FUSED = 1;
/** Planted floor charge. Stays until it detonates or the room is torn down. */
export const Mine = {
  state: new Uint8Array(N),
  fuse: new Float32Array(N),
};
/** Damaging child that orbits a player (Rune Ward blades). Not a projectile, so room wipes leave it. */
export const Orbiter = {
  owner: new Int32Array(N),
  angle: new Float32Array(N),
  radius: new Float32Array(N),
  spin: new Float32Array(N),
  cool: new Float32Array(N),
};

export const ALL_COMPONENTS = [
  Position,
  PrevPosition,
  Velocity,
  Radius,
  Health,
  TeamTag,
  Sprite,
  Anim,
  Lifetime,
  Damage,
  Pierce,
  Knockback,
  Player,
  Weapon,
  Enemy,
  Spawner,
  Pickup,
  Projectile,
  DragonTag,
  BossTag,
  FxTag,
  Dying,
  Mine,
  Orbiter,
];
